import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Grid, CircularProgress } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from "react";
import { db, storage } from "../../../../firebase/firebase";
import { doc, getDoc, setDoc, addDoc, collection, getDocs } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

/**
 * Modal genérico para crear o editar imágenes de cualquier colección.
 * @param {string} coleccion - Nombre de la colección en Firestore (ej: "comidas", "helados", "bebidas")
 */
const ModalCrearEditarItem = ({ open, onClose, idEditar = null, onSuccess, coleccion }) => {
    const [imagen, setImagen] = useState(null);
    const [existingImageUrl, setExistingImageUrl] = useState("");
    const [existingImagePath, setExistingImagePath] = useState("");
    const [loading, setLoading] = useState(false);
    const esEditar = idEditar !== null;

    useEffect(() => {
        if (esEditar && open) {
            cargarImagen();
        } else {
            limpiarFormulario();
        }
    }, [idEditar, open]);

    const cargarImagen = async () => {
        try {
            const docRef = doc(db, coleccion, idEditar);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setExistingImageUrl(data.url || "");
                if (data.url) {
                    const segments = data.url.split("%2F");
                    const fileName = segments[segments.length - 1].split("?")[0];
                    setExistingImagePath(`${coleccion}/${decodeURIComponent(fileName)}`);
                }
            }
        } catch (error) {
            console.error("Error al cargar la imagen:", error);
        }
    };

    const limpiarFormulario = () => {
        setImagen(null);
        setExistingImageUrl("");
        setExistingImagePath("");
    };

    const handleImageChange = (e) => {
        if (e.target.files[0]) setImagen(e.target.files[0]);
    };

    const handleGuardar = async () => {
        if (!imagen && !esEditar) {
            alert("Selecciona una imagen");
            return;
        }

        setLoading(true);
        try {
            let imageUrl = existingImageUrl;

            if (imagen) {
                if (existingImagePath) {
                    const oldRef = ref(storage, existingImagePath);
                    await deleteObject(oldRef).catch(() => {});
                }
                const newRef = ref(storage, `${coleccion}/${Date.now()}_${imagen.name}`);
                await uploadBytes(newRef, imagen);
                imageUrl = await getDownloadURL(newRef);
            }

            if (esEditar) {
                await setDoc(doc(db, coleccion, idEditar), { url: imageUrl }, { merge: true });
            } else {
                const colRef = collection(db, coleccion);
                const snapshot = await getDocs(colRef);
                const numero = snapshot.size + 1;
                await addDoc(colRef, {
                    url: imageUrl,
                    nombre: imagen.name,
                    fecha: new Date(),
                    numero
                });
            }

            onSuccess(esEditar ? "Imagen actualizada con éxito ✅" : "Imagen creada con éxito ✅");
            onClose();
            limpiarFormulario();
        } catch (error) {
            console.error("Error al guardar:", error);
            alert("Error al guardar la imagen: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {esEditar ? "Editar Imagen" : "Crear Nueva Imagen"}
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Grid container direction="column" alignItems="center" spacing={2} sx={{ pt: 2 }}>
                    {(imagen || existingImageUrl) && (
                        <Grid item xs={12} sx={{ width: '100%' }}>
                            <img
                                src={imagen ? URL.createObjectURL(imagen) : existingImageUrl}
                                alt="Preview"
                                style={{
                                    width: "100%",
                                    maxHeight: 300,
                                    objectFit: "cover",
                                    borderRadius: 12,
                                    border: '2px solid #e0e0e0'
                                }}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sx={{ width: '100%' }}>
                        <Button component="label" variant="outlined" fullWidth size="large">
                            {existingImageUrl || imagen ? "Cambiar Imagen" : "Seleccionar Imagen"}
                            <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                        </Button>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} disabled={loading}>Cancelar</Button>
                <Button variant="contained" color="primary" onClick={handleGuardar} disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : (esEditar ? "Actualizar" : "Crear")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalCrearEditarItem;
